import React from 'react';

export type LogoVariant = 'dark' | 'light' | 'white' | 'black';

interface LogoProps extends React.SVGProps<SVGSVGElement> {
    className?: string;
    size?: number;
    variant?: LogoVariant;
}

export function Logo({ className = '', size = 48, variant = 'dark', ...props }: LogoProps) {
    // Official Palette Colors
    const AURORA_GREEN = '#1DB954';
    const GREEN_500 = '#22C55E';
    const GREEN_400 = '#4ADE80';
    const DARK_GREEN = '#004322';
    const SONIC_CYAN = '#06B6D4'; // Only audio

    let outlineColor = AURORA_GREEN;
    let fillColor = SONIC_CYAN;

    if (variant === 'light') {
        outlineColor = DARK_GREEN;
        fillColor = DARK_GREEN;
    } else if (variant === 'white') {
        outlineColor = '#FFFFFF';
        fillColor = '#FFFFFF';
    } else if (variant === 'black') {
        outlineColor = '#000000';
        fillColor = '#000000';
    }

    const isDark = variant === 'dark';

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="TrustboxAI logo"
            className={className}
            {...props}
        >
            {isDark && (
                <defs>
                    <linearGradient id="glow-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={GREEN_400} />
                        <stop offset="100%" stopColor={AURORA_GREEN} />
                    </linearGradient>
                    <linearGradient id="audio-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="100%" stopColor={AURORA_GREEN} />
                    </linearGradient>
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                </defs>
            )}

            {/* Geometric Shield / Box Outline (Strictly matching the isometric outline from the image) */}
            <path
                d="M50 10 L85 30 V 70 L50 90 L15 70 V 30 Z"
                fill="none"
                stroke={isDark ? "url(#glow-grad)" : outlineColor}
                strokeWidth="4"
                strokeLinejoin="round"
                filter={isDark ? "url(#glow)" : undefined}
            />
            {/* Inner box lines to create 3D isometric flat feel. 
                NOTE: We skip the bottom vertical center line so the soundwave shines through optimally
                just like the image. */}
            <path
                d="M15 30 L50 48 L85 30"
                fill="none"
                stroke={isDark ? "url(#glow-grad)" : outlineColor}
                strokeWidth="4"
                strokeLinejoin="round"
                strokeOpacity="0.5"
            />

            {/* Animated Waveform Group (Exactly 9 bars as shown in target image) */}
            <g fill={isDark ? "url(#audio-grad)" : fillColor}>
                {/* Bar 1 (Dot) */}
                <rect x="20.5" y="48" width="3.5" height="4" rx="1.5">
                    <animate attributeName="y" values="48; 46; 48" dur="1.2s" begin="0s" repeatCount="indefinite" />
                    <animate attributeName="height" values="4; 8; 4" dur="1.2s" begin="0s" repeatCount="indefinite" />
                </rect>
                {/* Bar 2 */}
                <rect x="27.5" y="42" width="3.5" height="16" rx="1.5">
                    <animate attributeName="y" values="42; 38; 42" dur="1.2s" begin="0.1s" repeatCount="indefinite" />
                    <animate attributeName="height" values="16; 24; 16" dur="1.2s" begin="0.1s" repeatCount="indefinite" />
                </rect>
                {/* Bar 3 */}
                <rect x="34.5" y="32" width="3.5" height="36" rx="1.5">
                    <animate attributeName="y" values="32; 25; 32" dur="1.2s" begin="0.2s" repeatCount="indefinite" />
                    <animate attributeName="height" values="36; 50; 36" dur="1.2s" begin="0.2s" repeatCount="indefinite" />
                </rect>
                {/* Bar 4 */}
                <rect x="41.5" y="27.5" width="3.5" height="45" rx="1.5">
                    <animate attributeName="y" values="27.5; 18; 27.5" dur="1.2s" begin="0.3s" repeatCount="indefinite" />
                    <animate attributeName="height" values="45; 64; 45" dur="1.2s" begin="0.3s" repeatCount="indefinite" />
                </rect>
                {/* Bar 5 (Center - Longest) */}
                <rect x="48.25" y="20" width="3.5" height="60" rx="1.5">
                    <animate attributeName="y" values="20; 10; 20" dur="1.2s" begin="0.4s" repeatCount="indefinite" />
                    <animate attributeName="height" values="60; 80; 60" dur="1.2s" begin="0.4s" repeatCount="indefinite" />
                </rect>
                {/* Bar 6 */}
                <rect x="55" y="27.5" width="3.5" height="45" rx="1.5">
                    <animate attributeName="y" values="27.5; 18; 27.5" dur="1.2s" begin="0.5s" repeatCount="indefinite" />
                    <animate attributeName="height" values="45; 64; 45" dur="1.2s" begin="0.5s" repeatCount="indefinite" />
                </rect>
                {/* Bar 7 */}
                <rect x="62" y="32" width="3.5" height="36" rx="1.5">
                    <animate attributeName="y" values="32; 25; 32" dur="1.2s" begin="0.6s" repeatCount="indefinite" />
                    <animate attributeName="height" values="36; 50; 36" dur="1.2s" begin="0.6s" repeatCount="indefinite" />
                </rect>
                {/* Bar 8 */}
                <rect x="69" y="42" width="3.5" height="16" rx="1.5">
                    <animate attributeName="y" values="42; 38; 42" dur="1.2s" begin="0.7s" repeatCount="indefinite" />
                    <animate attributeName="height" values="16; 24; 16" dur="1.2s" begin="0.7s" repeatCount="indefinite" />
                </rect>
                {/* Bar 9 (Dot) */}
                <rect x="76" y="48" width="3.5" height="4" rx="1.5">
                    <animate attributeName="y" values="48; 46; 48" dur="1.2s" begin="0.8s" repeatCount="indefinite" />
                    <animate attributeName="height" values="4; 8; 4" dur="1.2s" begin="0.8s" repeatCount="indefinite" />
                </rect>
            </g>
        </svg>
    );
}

export default Logo;
